import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Menu, User, LogOut, ShieldCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const navItems = [
  { to: "/", label: "Acasă" },
  { to: "/comanda", label: "Comandă" },
  { to: "/pachete", label: "Pachete" },
  { to: "/recenzii", label: "Recenzii" },
  { to: "/track", label: "Track" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-link">
          <span className="font-display text-xl font-black tracking-tighter text-bone group-hover:text-crimson transition-colors">
            TUNE<span className="text-crimson">×</span>FORUS
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 hidden sm:inline">道</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="main-nav">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `font-mono uppercase text-xs tracking-[0.2em] transition-colors ${
                  isActive ? "text-crimson" : "text-white/70 hover:text-white"
                }`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="user-menu-button" className="text-white/80 hover:text-white">
                  <User className="w-4 h-4 mr-2" /> {user.name?.split(" ")[0]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-ink-800 border-white/10 text-bone">
                <DropdownMenuLabel className="font-mono text-xs uppercase">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => navigate("/cont")} data-testid="menu-account">Comenzile mele</DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="w-4 h-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Deconectare
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" size="sm" data-testid="login-link" className="text-white/80 hover:text-white">Cont</Button>
              </Link>
              <Link to="/comanda">
                <Button size="sm" data-testid="cta-order-header" className="bg-crimson hover:bg-crimson-light text-white rounded-none font-mono uppercase text-xs tracking-widest">
                  Comandă
                </Button>
              </Link>
            </>
          )}

          <Sheet>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle" aria-label="Deschide meniul">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-ink-800 border-white/10 text-bone">
              <div className="mt-8 flex flex-col gap-3">
                {navItems.map((it) => (
                  <SheetClose asChild key={it.to}>
                    <NavLink to={it.to} className="font-mono uppercase text-sm tracking-widest text-white/80 hover:text-crimson py-2">
                      {it.label}
                    </NavLink>
                  </SheetClose>
                ))}
                <div className="mt-4 pt-4 border-t border-white/10">
                  {user ? (
                    <div className="flex flex-col gap-2">
                      <SheetClose asChild>
                        <Button
                          variant="outline"
                          onClick={() => navigate("/cont")}
                          className="w-full justify-start rounded-none border-white/15 bg-transparent text-white hover:bg-white hover:text-black"
                        >
                          <User className="w-4 h-4 mr-2" />
                          Contul meu
                        </Button>
                      </SheetClose>
                      {user.role === "admin" && (
                        <SheetClose asChild>
                          <Button
                            variant="outline"
                            onClick={() => navigate("/admin")}
                            className="w-full justify-start rounded-none border-white/15 bg-transparent text-white hover:bg-white hover:text-black"
                          >
                            <ShieldCheck className="w-4 h-4 mr-2" />
                            Admin Panel
                          </Button>
                        </SheetClose>
                      )}
                      <SheetClose asChild>
                        <Button
                          variant="ghost"
                          onClick={() => { logout(); navigate("/"); }}
                          className="w-full justify-start rounded-none text-white/70 hover:text-white"
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          Deconectare
                        </Button>
                      </SheetClose>
                    </div>
                  ) : (
                    <SheetClose asChild>
                      <Link to="/login">
                        <Button data-testid="mobile-login-link" className="w-full rounded-none bg-crimson hover:bg-crimson-light text-white font-mono uppercase tracking-widest text-xs">
                          Log in
                        </Button>
                      </Link>
                    </SheetClose>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
